import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  api,
  clearToken,
  clearUserToken,
  clearStoredUser,
  getStoredUser,
} from "@/lib/api";
import {
  MapPin,
  Gift,
  LogOut,
  Copy,
  Sparkles,
  Users,
  Sun,
  Moon,
  Monitor,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTheme } from "@/lib/theme";

export default function UserHome() {
  const nav = useNavigate();
  const { mode, setMode } = useTheme();
  const [me, setMe] = useState(getStoredUser());
  const [ref, setRef] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyCode, setApplyCode] = useState("");
  const [applying, setApplying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [meRes, refRes] = await Promise.all([
        api.get("/auth/me"),
        api.get("/referrals/me"),
      ]);
      setMe(meRes.data);
      setRef(refRes.data);
    } catch (err) {
      // 401 is handled by the axios interceptor (which bounces to /login).
      // Any other error is unexpected — surface it in the console so the dev
      // notices, but don't block the UI (setLoading is unconditional in `finally`).
      if (err?.response?.status !== 401) {
        console.error("UserHome load failed:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Server-side logout failure is non-blocking — we always clear local state.
      console.warn("Backend logout failed (continuing with local sign-out):", err);
    }
    clearToken();
    clearUserToken();
    clearStoredUser();
    toast.success("Signed out");
    nav("/login", { replace: true });
  };

  const shareLink = ref?.code
    ? `${window.location.origin}/register?ref=${ref.code}`
    : "";

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  const submitCode = async (e) => {
    e.preventDefault();
    if (!applyCode.trim()) return;
    setApplying(true);
    try {
      await api.post("/referrals/apply-code", { code: applyCode.trim() });
      toast.success("+50 points!");
      setApplyCode("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not apply code");
    } finally {
      setApplying(false);
    }
  };

  const themes = [
    { v: "light", icon: Sun, label: "Light" },
    { v: "system", icon: Monitor, label: "System" },
    { v: "dark", icon: Moon, label: "Dark" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 glass border-b border-border">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center shadow-md shadow-primary/20">
              <MapPin className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <span className="text-base font-extrabold tracking-tight">NearMe</span>
          </Link>
          <Button
            variant="ghost"
            onClick={logout}
            data-testid="user-logout-btn"
            className="h-9"
          >
            <LogOut className="w-4 h-4 mr-1.5" /> Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        {loading || !me ? (
          <div className="text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* Profile */}
            <section className="mb-8 animate-in-up" data-testid="user-profile">
              <div className="text-xs font-bold uppercase tracking-widest text-primary mb-2">
                My account
              </div>
              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-1">
                Hi, {me.name} 👋
              </h1>
              <p className="text-sm text-muted-foreground">
                {me.email} · Role: <span className="font-semibold">{me.role}</span>
                {me.city ? ` · ${me.city}` : ""}
              </p>
            </section>

            {/* Referral card */}
            <section
              data-testid="referral-card"
              className="bg-primary text-primary-foreground rounded-2xl p-6 sm:p-8 shadow-xl shadow-primary/20 mb-8"
            >
              <div className="flex items-start gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
                  <Gift className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest opacity-80">
                    Rewards
                  </div>
                  <div className="text-xl font-extrabold leading-tight">
                    Invite friends. Earn points.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div>
                  <div className="text-[10px] uppercase tracking-widest opacity-70">
                    Your points
                  </div>
                  <div className="text-4xl font-extrabold" data-testid="user-points">
                    {ref?.points ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest opacity-70">
                    Friends referred
                  </div>
                  <div className="text-4xl font-extrabold" data-testid="user-referrals">
                    {ref?.referred_users?.length ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest opacity-70">
                    Points per invite
                  </div>
                  <div className="text-4xl font-extrabold">50</div>
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">
                  Your code
                </div>
                <div
                  className="mono text-2xl font-extrabold tracking-wider bg-white/15 rounded-lg px-4 py-3 flex items-center justify-between"
                  data-testid="user-ref-code"
                >
                  <span>{ref?.code || "—"}</span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => copy(ref?.code || "")}
                    className="bg-white text-primary hover:bg-white/90"
                    data-testid="copy-code-btn"
                  >
                    <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
                  </Button>
                </div>
                {shareLink && (
                  <div className="mt-3 text-xs opacity-80 break-all">
                    Share link:{" "}
                    <button
                      onClick={() => copy(shareLink)}
                      className="underline font-semibold"
                      data-testid="copy-link-btn"
                    >
                      {shareLink}
                    </button>
                  </div>
                )}
              </div>
            </section>

            {/* Apply someone else's code */}
            {ref && !me.referred_by && (
              <section className="bg-card border border-border rounded-2xl p-6 mb-8" data-testid="apply-code-section">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-accent text-accent-foreground flex items-center justify-center">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-sm font-bold">Got invited?</div>
                    <div className="text-xs text-muted-foreground">
                      Enter a friend&apos;s code to claim +50 points.
                    </div>
                  </div>
                </div>
                <form onSubmit={submitCode} className="flex gap-2">
                  <Input
                    value={applyCode}
                    onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
                    placeholder="e.g. ABC12345"
                    className="h-11 mono uppercase"
                    data-testid="apply-code-input"
                  />
                  <Button
                    type="submit"
                    disabled={applying}
                    className="h-11"
                    data-testid="apply-code-btn"
                  >
                    {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                  </Button>
                </form>
              </section>
            )}

            {/* Referred users */}
            {ref?.referred_users?.length > 0 && (
              <section className="bg-card border border-border rounded-2xl p-6 mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-4 h-4 text-primary" />
                  <div className="text-sm font-bold">Friends you referred</div>
                </div>
                <div className="space-y-2">
                  {ref.referred_users.map((u) => (
                    <div
                      key={u.user_id}
                      className="flex items-center justify-between p-3 rounded-lg bg-secondary/40 text-sm"
                    >
                      <div>
                        <div className="font-semibold">{u.name}</div>
                        <div className="text-xs text-muted-foreground">{u.email}</div>
                      </div>
                      <div className="text-xs font-bold text-primary">+50</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Theme */}
            <section className="bg-card border border-border rounded-2xl p-6">
              <div className="text-sm font-bold mb-3">Appearance</div>
              <div className="flex gap-1 bg-secondary rounded-lg p-1">
                {themes.map((o) => (
                  <button
                    key={o.v}
                    onClick={() => setMode(o.v)}
                    data-testid={`user-theme-${o.v}`}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold transition-all ${
                      mode === o.v
                        ? "bg-card text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <o.icon className="w-3.5 h-3.5" />
                    {o.label}
                  </button>
                ))}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
